import {
	GridRect,
	rectanglesOverlap,
} from "@/modules/dashboards/card-displacement";

import DashboardDO = App.Data.DashboardDO;

export const ROWS_PER_PAGE = 24;

export interface DashboardPage {
	cards: DashboardPageCard[];
}

export interface DashboardPageCard {
	index: number;
	card: DashboardDO["cards"][0];
}

export function canPrintDashboard(dashboard: DashboardDO): boolean {
	try {
		groupCardsIntoPages(dashboard);

		return true;
	} catch (e) {
		return false;
	}
}

/**
 * Float every card up to remove the vertical gaps left by cards that were hidden
 * from an export (e.g. by their alert visibility condition), preserving each card's
 * column and relative order. Cards constrained to a single page (use_card_layout_height)
 * are snapped down to the next page rather than straddling a page boundary, so the
 * result still satisfies groupCardsIntoPages. Returns a new array of new card objects;
 * the input is not mutated.
 */
export function compactCardsForExport(
	cards: DashboardDO["cards"],
	rowsPerPage: number = ROWS_PER_PAGE
): DashboardDO["cards"] {
	const sorted = cards.toSorted(
		(a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x
	);

	const placed: GridRect[] = [];

	return sorted.map((card) => {
		const { x, w, h } = card.layout;
		let y = 0;

		// Find the highest position where the card neither overlaps an already-placed
		// card nor (when it must stay on one page) straddles a page boundary.
		while (true) {
			const straddlesPage =
				h <= rowsPerPage &&
				Math.floor(y / rowsPerPage) !== Math.floor((y + h - 1) / rowsPerPage);

			if (straddlesPage && card.use_card_layout_height) {
				y = (Math.floor(y / rowsPerPage) + 1) * rowsPerPage;
				continue;
			}

			const rect: GridRect = { x, y, w, h };
			if (placed.some((other) => rectanglesOverlap(rect, other))) {
				y++;
				continue;
			}

			placed.push(rect);
			return { ...card, layout: { ...card.layout, y } };
		}
	});
}

export function groupCardsIntoPages(dashboard: DashboardDO): DashboardPage[] {
	// Sort cards by their y coordinate
	const sortedCards = dashboard.cards.sort((a, b) => a.layout.y - b.layout.y);

	const pages: Record<number, DashboardPage> = {};

	sortedCards.forEach((card, idx) => {
		const cardBottom = (card.layout.y % ROWS_PER_PAGE) + card.layout.h;

		// If a card is supposed to use the card height, and overlaps throw an error.
		// Tables overlapping pages are supported.
		if (cardBottom > ROWS_PER_PAGE && card.use_card_layout_height) {
			throw new Error(`Card with id ${card.id} overlaps a page`);
		}

		const pageNumber = Math.floor(card.layout.y / ROWS_PER_PAGE);
		if (pages[pageNumber] === undefined) {
			pages[pageNumber] = { cards: [] };
		}

		// Store cards using original index as key, this can be used to update the cards later on.
		pages[pageNumber].cards.push({
			index: idx,
			card: {
				...card,
				layout: {
					...card.layout,
					y: card.layout.y % ROWS_PER_PAGE,
				},
			},
		});
	});

	return Object.values(pages);
}
